import React, { FC } from 'react';
import TableSimple from 'src/components/TableSimple';
import { CustomRenderType, TableColumnType, TableRowType } from 'src/types/table';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import translate from '@translate';
import { ProductSetItemType } from 'src/types/product';
import TextFieldDebounced from 'src/components/TextFieldDebounced';
import ProductsSelect from 'src/components/ProductsSelect';
import { sizes } from 'src/themes/default';
import { FormErrorsType } from '../../types/global';

export type EditKeysType = 'PRODUCT_COUNT' | 'PRODUCT_SKU';

const allColumns: TableColumnType[] = [
    {
        key: 'PRODUCT_SKU',
        label: 'sku',
        defaultShow: true,
        customRender: true,
        customStyle: { width: '700px', minWidth: '700px' },
    },
    {
        key: 'PRODUCT_NAME',
        label: 'name',
        defaultShow: true,
        customRender: true,
    },
    {
        key: 'PRODUCT_EAN',
        label: 'ean',
        defaultShow: true,
        customRender: true,
    },
    {
        key: 'PRODUCT_COUNT',
        label: 'quantity',
        defaultShow: true,
        customRender: true,
        customStyle: { width: '80px', minWidth: '80px'},
    },
];

type ProductSetItemsTablePropsType = {
    data?: ProductSetItemType[];
    onChange: (value: any) => void;
    disableEdit?: boolean;
    editErrors: FormErrorsType;
};

const ProductSetItemsTable: FC<ProductSetItemsTablePropsType> = (props) => {
    const handleRemoveRow = (row: TableRowType) => {
        props.onChange(
            props.data?.filter((item) => item.ID != row.ID)
        );
    };

    const getInputName = (id: number, key: string) => `product-set-item-${id}-${key}`;

    const changeValue = (key: EditKeysType, value: any, id: number) => {
        const newData = props.data?.map((product) => {
            if (product.ID === id) {
                const updatedProduct = { ...product };
                if (key === 'PRODUCT_COUNT') {
                    updatedProduct.PRODUCT_COUNT = value;
                } else if (key === 'PRODUCT_SKU') {
                    updatedProduct.PRODUCT_SKU = value;
                }
                return updatedProduct;
            }
            return product;
        });
        props.onChange(newData);
    };

    const customRender: CustomRenderType = (column, row) => {
        const name = getInputName(row.ID, column.key);
        switch (column.key) {
            case 'PRODUCT_SKU':
                return (
                    <ProductsSelect
                        idKey="SKU"
                        value={row.PRODUCT_SKU}
                        error={props.editErrors[name]}
                        onChange={(value, error) => {
                            if (!Array.isArray(value)) {
                                changeValue('PRODUCT_SKU', value?.SKU, row.ID);
                            }
                        }}
                        name={name}
                        fullWidth
                        required
                        disabled={props.disableEdit}
                    />
                );
            case 'PRODUCT_COUNT':
                return (
                    <TextFieldDebounced
                        value={row.PRODUCT_COUNT}
                        error={props.editErrors[name]}
                        onChange={(value, error) =>
                            changeValue('PRODUCT_COUNT', (value || value === 0) && String(value), row.ID)
                        }
                        name={name}
                        fullWidth
                        type="numberLocale"
                        min={1}
                        required
                        disabled={props.disableEdit}
                    />
                );
            case 'PRODUCT_NAME':
                return row.PRODUCT_ITEM?.NAME;
            case 'PRODUCT_EAN':
                return row.PRODUCT_ITEM?.EAN;
        }
    };

    return (
        <TableSimple
            rowIdKey="ID"
            rows={props.data || []}
            columsLabelPrefix="productDetail.productSetTab.table"
            allColumns={allColumns}
            initSort={{ column: 'ID', direction: 'ASC', createIdNumber: true }}
            rowActions={
                props.disableEdit
                    ? undefined
                    : [
                          {
                              icon: <HighlightOffIcon />,
                              label: translate('productDetail.productSetTab.removeProduct'),
                              onClick: handleRemoveRow,
                              style: { height: sizes.table_action_with_input_H },
                              hiddenFce: () => !!props.disableEdit,
                          },
                      ]
            }
            disableFilter
            customRender={customRender}
            sumCols={{ PRODUCT_COUNT: true }}
            maxHeight={'unset'}
        />
    );
};

export default ProductSetItemsTable;
